import React, { useEffect, useState, useContext } from "react";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
// import { Tabs } from "../../../components/tabs";
import { TextInput } from "../../../components/textInput";
import { ImageInput } from "../../../components/imageInput";
import { Checkbox } from "../../../components/checkbox";
import { Toast } from "../../../components/toast";
import { Button } from "../../../components/button";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContext } from "../../../contexts/AuthContext";
import logo from "../../../assets/logo-mark.svg";
import bgImage from "../../../assets/bg/simple-opaque.png";
import { useNavigate } from "react-router-dom";
import { PageHeading } from "../../../components/pageHeading";
import { z } from "zod";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  allow_unscheduled_shifts: z
    .union([z.boolean(), z.number()])
    .transform((val) => Boolean(val))
    .optional(),
  geofence_enforcement: z.enum(["off", "soft", "hard"]).optional(),
});

function Organization() {
  const { getToken, fetchUserData, authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();
  const initializationComplete =
    authContext.organization.initializationComplete;
  const navigate = useNavigate();
  const [isFetching, setIsFetching] = useState(true);
  const [backendData, setBackendData] = useState({
    name: "",
    logo_url: "",
    initialization_complete: 0,
    allow_unscheduled_shifts: false,
    geofence_enforcement: "soft",
  });
  const [priceId, setPriceId] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(true);

  setTimeout(() => {
    setIsPageLoading(false);
  }, 1200);

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      logo_url: "",
      allow_unscheduled_shifts: false,
      geofence_enforcement: "soft",
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit, setValue, register } = methods;

  /// Toast ///
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const handleResponse = (message, type) => {
    setToast({ show: true, message, type });
    if (backendData.initialization_complete === 0) {
      setTimeout(() => {
        navigate("/");
      });
    }
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const onSubmit = (data) => {
    return updateOrganization(data);
  };

  /// Handle success ///
  const onSuccess = async () => {
    try {
      await fetchUserData();
    } catch (error) {
      console.error("Error in fetchUserData:", error);
    }
    handleResponse("Organization details updated successfully", "success");
    if (sessionStorage.getItem("selectedPrice")) {
      sessionStorage.removeItem("selectedPrice");
    }
  };

  /// Images ///
  const [imageFile, setImageFile] = useState(null);

  /// Fetch organization details ///
  const fetchOrganization = () => {
    if (!orgId) {
      return;
    }

    authFetch(`${process.env.REACT_APP_API_URL}/v1/organizations/${orgId}`)
      .then((response) => {
        if (!response.ok)
          throw new Error(`Fetch returned status ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setBackendData({
          name: data.name,
          logo_url: data.logo_url,
          initialization_complete: data.initialization_complete,
          stripe_customer_id: data.stripe_customer_id,
          allow_unscheduled_shifts: data.allow_unscheduled_shifts,
          geofence_enforcement: data.geofence_enforcement || "soft",
        });
        setValue("name", data.name);
        setValue(
          "allow_unscheduled_shifts",
          Boolean(data.allow_unscheduled_shifts),
        );
        setValue("geofence_enforcement", data.geofence_enforcement || "soft");

        setIsFetching(false);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  useEffect(() => {
    fetchOrganization();
  }, [orgId]);

  // useEffect(() => {
  //   if (isFetching === false && backendData.initialization_complete === 0) {
  //     setIsInitializing(true);
  //   }
  // }, [isFetching, backendData]);

  /// Update user ///
  function updateOrganization(data) {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key !== "customers") {
        // Convert boolean to string for allow_unscheduled_shifts
        if (key === "allow_unscheduled_shifts") {
          formData.append(key, data[key] ? "1" : "0");
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    if (imageFile) {
      formData.append("file", imageFile);
    } else if (backendData.logo_url) {
      formData.append("logo", backendData.logo_url);
    }

    if (priceId) {
      formData.append("price_id", priceId);
    }

    if (backendData.stripe_customer_id) {
      formData.append("stripe_customer_id", backendData.stripe_customer_id);
    }

    const url = !initializationComplete
      ? `${process.env.REACT_APP_API_URL}/v1/organizations/${orgId}?initializing=true`
      : `${process.env.REACT_APP_API_URL}/v1/organizations/${orgId}`;

    authFetch(url, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Fetch returned status ${response.status}`);
        return response.json();
      })
      .then(() => {
        onSuccess();
      });
  }

  const onError = (errors, event) => {};

  /// Image upload ///
  function handleImageChange(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    setImageFile(file);
  }

  /// Get and set the price id passed via registration ///
  useEffect(() => {
    // Check if we're done initializing and the price is available
    if (!initializationComplete) {
      const storedPriceId = sessionStorage.getItem("selectedPrice");
      if (storedPriceId) {
        setPriceId(storedPriceId);
      } else {
        setPriceId(process.env.REACT_APP_STARTER_PLAN);
      }
    }
  }, [initializationComplete]); // Only run this effect when `initializationComplete` changes

  return !isFetching ? (
    <div
      className={` ${
        !initializationComplete
          ? "relative bg-no-repeat h-screen bg-left-bottom"
          : ""
      }`}
      style={
        !initializationComplete
          ? {
              backgroundImage: `url(${bgImage})`,
              backgroundSize: "25%", // Adjust size of the image (50% of its original size)
              backgroundPosition: "calc(100% - 10px) calc(100% + 200px)",
            }
          : {}
      }
    >
      {/* {initializationComplete && (
        <>
          <Tabs current="Organization" section="settings" />
        </>
      )} */}

      {/* <Tabs current="Activity" section="reporting" /> */}
      <main className="py-6">
        {!initializationComplete && (
          <div className="px-4 sm:px-6 lg:px-6">
            <div className="flex items-center">
              <div className="flex-1 py-6">
                <div className="space-y-6 max-w-md">
                  <div className="mt-16">
                    <img src={logo} alt="Opspot" className="w-12" />
                  </div>
                  <p className="text-sm text-gray-500">
                    Welcome to Opspot! What can we call you?
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="px-4 sm:px-6 lg:px-6">
          {!!initializationComplete && <PageHeading title="Organization" />}
          <div className="flex items-center mb-4">
            <FormProvider {...methods}>
              <div className="flex-1 py-6">
                <div className="space-y-8 max-w-md">
                  <TextInput name="name" label="Organization name" />
                  <ImageInput
                    name="logo"
                    label="Logo (optional)"
                    onChange={handleImageChange}
                    accept="image/png, image/jpeg, image/gif, image/svg+xml, image/webp"
                    maxFileSize={3201010}
                    existing={backendData.logo_url}
                  />
                  {/* Clock-in policy — grouped so the page's space-y-8 doesn't
                      push the geofence menu away from the checkbox */}
                  <div className="space-y-3">
                    <Checkbox
                      name="allow_unscheduled_shifts"
                      label="Allow users to clock in to unscheduled shifts"
                    />

                    <div>
                      <label
                        htmlFor="geofence_enforcement"
                        className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300"
                      >
                        Clock-in geofencing
                      </label>
                      <select
                        id="geofence_enforcement"
                        {...register("geofence_enforcement")}
                        className="block w-full rounded-md border-0 px-3 py-2.5 text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 focus:outline-none text-sm"
                      >
                        <option value="off">
                          Off - Don't check the guard's site
                        </option>
                        <option value="soft">
                          Warn - Allow clocking in, but flag site
                        </option>
                        <option value="hard">
                          Block - Require guard's to clock-in within the
                          geofence
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="flex">
                    <Button onClick={handleSubmit(onSubmit)}>Save</Button>
                  </div>
                </div>
              </div>
            </FormProvider>

            <Toast
              show={toast.show}
              message={toast.message}
              type={toast.type}
              onHide={hideToast}
            />
          </div>
        </div>
      </main>
    </div>
  ) : null;
}

export { Organization };
